import type Account from '@/data/account/model';
import { hasOnboardedAttendance } from '@/data/attendance/selectors/hasAttendance';
import type Plan from '@/data/plan/model';
import type User from '@/data/user/model';
import { MenuItem } from '@/shared/components/nav/MenuItem';
import MenuItems from '@/shared/components/nav/MenuItems';
import Features from '@/shared/features';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwSelector } from '@/store';

type AppSettingsMenuProps = {
  authUser: User;
  authAccount: Account;
  accountPlan: Plan;
};

type TrackingType =
  | 'general_settings'
  | 'time_off_settings'
  | 'scheduling_settings'
  | 'attendance_settings'
  | 'integrations'
  | 'account_billing';

const AppSettingsMenuItem = ({ authUser, authAccount, accountPlan }: AppSettingsMenuProps) => {
  const billingUrl = accountPlan.isTrial() ? authAccount.plansUrl() : authAccount.billingUrl();
  const isAttendanceOnboarded = useWiwSelector(hasOnboardedAttendance);

  function trackOnClick(type: TrackingType) {
    posthogCapture(`top_nav.gear.${type}`);
  }

  return (
    <MenuItem className="menu-icon" hideLabel="desktop" icon="gear" label="Settings" dropdown>
      <MenuItems dropdownMenu>
        <MenuItem
          data-testid="top-nav-general-settings"
          onClick={() => trackOnClick('general_settings')}
          exact
          icon="settings"
          label="General Settings"
          show={!authAccount.isWorkchatFree()}
          to="/settings"
        />

        <MenuItem
          data-testid="top-nav-time-off-settings"
          onClick={() => trackOnClick('time_off_settings')}
          icon="no-time-off"
          label="Time Off Settings"
          show={authAccount.hasFeature(Features.TIME_OFF_REQUESTS, true)}
          to="/settings/timeoff"
        />

        <MenuItem
          data-testid="top-nav-scheduling-settings"
          onClick={() => trackOnClick('scheduling_settings')}
          icon="scheduling-settings"
          label="Scheduling Settings"
          show={authAccount.hasScheduling()}
          to="/settings/scheduling"
        />

        <MenuItem
          data-testid="top-nav-attendance-settings"
          onClick={() => trackOnClick('attendance_settings')}
          icon="attendance-settings"
          label="Attendance Settings"
          show={authAccount.hasAttendance()}
          to={isAttendanceOnboarded ? '/settings/attendance' : '/attendance/get-started'}
        />

        <MenuItem
          data-testid="top-nav-integrations"
          onClick={() => trackOnClick('integrations')}
          icon="integrations"
          label="Integrations"
          to="/settings/integrations"
        />

        <MenuItem
          data-testid="top-nav-account-billing"
          onClick={() => trackOnClick('account_billing')}
          icon="billing"
          label="Account & Billing"
          show={authUser.canBilling()}
          to={billingUrl}
        />
      </MenuItems>
    </MenuItem>
  );
};

export default AppSettingsMenuItem;
